import { MouseEvent, useCallback, useRef } from "react";

const PRIMARY_MOUSE_BUTTON = 0;

interface BackdropDismissHandlers {
  readonly onMouseDown: (event: MouseEvent<HTMLElement>) => void;
  readonly onMouseUp: (event: MouseEvent<HTMLElement>) => void;
}

/**
 * Dismisses an overlay only when a press both starts and ends on the backdrop
 * itself, so an accidental press can still be taken back by releasing
 * elsewhere.
 *
 * Both ends are checked on the press events rather than on a click, because a
 * click fires on the nearest common ancestor of where the press started and
 * ended. Dragging out of the dialog to select text, or back into it to think
 * again, therefore reports a click on the backdrop and would throw away
 * whatever the user had typed.
 */
export const useBackdropDismiss = (
  onDismiss: () => void,
): BackdropDismissHandlers => {
  const pressStartedOnBackdropRef = useRef(false);

  const onMouseDown = useCallback((event: MouseEvent<HTMLElement>): void => {
    // Unlike a click, a mouse press reports every button, and only the primary
    // one dismisses.
    pressStartedOnBackdropRef.current =
      event.button === PRIMARY_MOUSE_BUTTON &&
      event.target === event.currentTarget;
  }, []);

  const onMouseUp = useCallback(
    (event: MouseEvent<HTMLElement>): void => {
      const pressStartedOnBackdrop = pressStartedOnBackdropRef.current;
      pressStartedOnBackdropRef.current = false;
      if (!pressStartedOnBackdrop) return;
      if (event.target !== event.currentTarget) return;
      onDismiss();
    },
    [onDismiss],
  );

  return { onMouseDown, onMouseUp };
};
